import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ServiceService } from '@/services/serviceService'
import { useAuthStore } from './auth'
import api, { isSessionExpiredError } from '@/services/api'
import { ServiceStatus } from '@/services/serviceService'
import type {
  ServiceListItem,
  ServiceDetails,
  ServiceSearchParams,
  ServiceSearchResponse,
  ServiceCategory,
  Location,
  CreateServiceRequest,
  UpdateServiceRequest,
  CreateLocationRequest,
  UpdateLocationRequest,
  // ServiceVisibility removed
  ServiceImage,
  ServiceAvailability,
  ServiceReview
} from '@/services/serviceService'

export const useServiceStore = defineStore('service', () => {
  // State
  const services = ref<ServiceListItem[]>([])
  const currentService = ref<ServiceDetails | null>(null)
  const categories = ref<ServiceCategory[]>([])
  const locations = ref<Location[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  
  // Search and pagination state
  const searchResults = ref<ServiceSearchResponse | null>(null)
  const searchParams = ref<ServiceSearchParams>({
    page: 1,
    pageSize: 10,
    sortBy: 'CreatedAt',
    sortDirection: 'desc'
  })

  // Computed
  const hasServices = computed(() => services.value.length > 0)
  const totalPages = computed(() => searchResults.value?.totalPages || 0)
  const currentPage = computed(() => searchParams.value.page || 1)
  const totalCount = computed(() => searchResults.value?.totalCount || 0)
  
  const activeServices = computed(() => 
    services.value.filter(service => service.isActive)
  )
  
  const draftServices = computed(() => 
    services.value.filter(service => service.status === ServiceStatus.Draft)
  )

  // Actions
  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const clearError = () => {
    error.value = null
  }

  // Search services
  const searchServices = async (params: ServiceSearchParams = {}) => {
    setLoading(true)
    clearError()

    try {
      const mergedParams = { ...searchParams.value, ...params }
      searchParams.value = mergedParams

      const response = await ServiceService.searchServices(mergedParams)
      searchResults.value = response
      services.value = response.services

      // Update categories if not already loaded
      if (response.categories && response.categories.length > 0) {
        categories.value = response.categories
      }

      return response
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search services'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Get service by ID
  const getServiceById = async (id: string) => {
    setLoading(true)
    clearError()

    try {
      const service = await ServiceService.getServiceById(id)
      currentService.value = service
      return service
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get service'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Get services by provider
  const getServicesByProvider = async (providerId: string, isActive?: boolean) => {
    setLoading(true)
    clearError()

    try {
      const providerServices = await ServiceService.getServicesByProvider(providerId, isActive)
      services.value = providerServices
      return providerServices
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get provider services'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Get my services (current provider)
  const getMyServices = async (isActive?: boolean) => {
    setLoading(true)
    clearError()

    try {
      const authStore = useAuthStore()
      if (!authStore.user?.id) {
        throw new Error('User not authenticated')
      }

      // Import provider store to get current provider ID
      const { useProviderStore } = await import('./provider')
      const providerStore = useProviderStore()

      // Ensure we have provider data loaded
      if (!providerStore.myProvider) {
        await providerStore.fetchMyProvider()
      }

      if (!providerStore.myProvider?.id) {
        throw new Error('Provider profile not found')
      }

      console.log('Loading services for provider:', providerStore.myProvider.businessName, 'ID:', providerStore.myProvider.id)

      const myServices = await ServiceService.getServicesByProvider(providerStore.myProvider.id, isActive)
      services.value = myServices

      console.log('Loaded services:', myServices.length, 'services')

      return myServices
    } catch (err) {
      console.error('Failed to load my services:', err)
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get my services'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Get featured services
  const getFeaturedServices = async (count: number = 10) => {
    setLoading(true)
    clearError()

    try {
      const featured = await ServiceService.getFeaturedServices(count)
      return featured
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to get featured services'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Create service
  const createService = async (serviceData: CreateServiceRequest) => {
    setLoading(true)
    clearError()
    
    try {
      const newService = await ServiceService.createService(serviceData)
      
      // Add to local state - refresh the list to get proper data
      await searchServices()
      
      return newService
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create service'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Update service
  const updateService = async (id: string, serviceData: UpdateServiceRequest) => {
    setLoading(true)
    clearError()
    
    try {
      const updatedService = await ServiceService.updateService(id, serviceData)
      
      // Update local state
      const index = services.value.findIndex(s => s.id === id)
      if (index !== -1) {
        services.value[index] = {
          ...services.value[index],
          name: updatedService.name,
          shortDescription: updatedService.shortDescription,
          categoryId: updatedService.categoryId,
          categoryName: updatedService.categoryName,
          price: updatedService.price,
          durationMinutes: updatedService.durationMinutes,
          isActive: updatedService.isActive,
          status: updatedService.status
        }
      }
      
      // Update current service if it's the same
      if (currentService.value?.id === id) {
        currentService.value = updatedService
      }
      
      return updatedService
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Toggle service status
  const toggleServiceStatus = async (id: string, activate: boolean) => {
    setLoading(true)
    clearError()
    
    try {
      const updatedService = await ServiceService.toggleServiceStatus(id, activate)
      
      // Update local state
      const index = services.value.findIndex(s => s.id === id)
      if (index !== -1) {
        services.value[index].isActive = updatedService.isActive
        services.value[index].status = updatedService.status
      }
      
      return updatedService
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle service status'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Publish service
  const publishService = async (id: string) => {
    setLoading(true)
    clearError()
    
    try {
      const publishedService = await ServiceService.publishService(id)
      
      // Update local state
      const index = services.value.findIndex(s => s.id === id)
      if (index !== -1) {
        services.value[index].status = publishedService.status
      }
      
      return publishedService
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish service'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Archive service
  const archiveService = async (id: string) => {
    setLoading(true)
    clearError()
    
    try {
      const archivedService = await ServiceService.archiveService(id)
      
      // Update local state
      const index = services.value.findIndex(s => s.id === id)
      if (index !== -1) {
        services.value[index].status = archivedService.status
      }
      
      return archivedService
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to archive service'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Delete service
  const deleteService = async (id: string) => {
    setLoading(true)
    clearError()
    
    try {
      await ServiceService.deleteService(id)
      
      // Remove from local state
      services.value = services.value.filter(s => s.id !== id)
      
      // Clear current service if it's the deleted one
      if (currentService.value?.id === id) {
        currentService.value = null
      }
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete service'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Load categories
  const loadCategories = async (isActive?: boolean) => {
    setLoading(true)
    clearError()

    try {
      const loadedCategories = await ServiceService.getServiceCategories(isActive)
      categories.value = loadedCategories
      return loadedCategories
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load categories'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Load my locations
  const loadMyLocations = async (isActive?: boolean) => {
    setLoading(true)
    clearError()

    try {
      const myLocations = await ServiceService.getMyLocations(isActive)
      locations.value = myLocations
      return myLocations
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load locations'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Load locations for a specific provider
  const loadLocationsForProvider = async (providerId: string, isActive?: boolean) => {
    setLoading(true)
    clearError()

    try {
      const providerLocations = await ServiceService.getLocationsByProvider(providerId, isActive)
      locations.value = providerLocations
      return providerLocations
    } catch (err) {
      // Don't set error for session expiration - already handled by API interceptor
      if (!isSessionExpiredError(err)) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load provider locations'
        setError(errorMessage)
      }
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Update search params
  const updateSearchParams = (params: Partial<ServiceSearchParams>) => {
    searchParams.value = { ...searchParams.value, ...params }
  }

  // Reset search
  const resetSearch = () => {
    searchParams.value = {
      page: 1,
      pageSize: 10,
      sortBy: 'CreatedAt',
      sortDirection: 'desc'
    }
    searchResults.value = null
    services.value = []
  }

  // Clear current service
  const clearCurrentService = () => {
    currentService.value = null
  }

  // Service Images Management
  const uploadServiceImages = async (serviceId: string, images: ServiceImage[]) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // const uploadedImages = await ServiceService.uploadServiceImages(serviceId, images)
      
      // Update current service if loaded
      if (currentService.value?.id === serviceId) {
        currentService.value.images = [...currentService.value.images, ...images]
      }
      
      return images
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload images'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateServiceImage = async (serviceId: string, imageId: string, imageData: Partial<ServiceImage>) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // const updatedImage = await ServiceService.updateServiceImage(serviceId, imageId, imageData)
      
      // Update current service if loaded
      if (currentService.value?.id === serviceId) {
        const imageIndex = currentService.value.images.findIndex(img => img.id === imageId)
        if (imageIndex !== -1) {
          currentService.value.images[imageIndex] = {
            ...currentService.value.images[imageIndex],
            ...imageData
          }
        }
      }
      
      return imageData as ServiceImage
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update image'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteServiceImage = async (serviceId: string, imageId: string) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // await ServiceService.deleteServiceImage(serviceId, imageId)
      
      // Update current service if loaded
      if (currentService.value?.id === serviceId) {
        currentService.value.images = currentService.value.images.filter(img => img.id !== imageId)
      }
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete image'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const reorderServiceImages = async (serviceId: string, imageIds: string[]) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // await ServiceService.reorderServiceImages(serviceId, imageIds)
      
      // Update current service if loaded
      if (currentService.value?.id === serviceId) {
        const reorderedImages = imageIds.map((id, index) => {
          const image = currentService.value!.images.find(img => img.id === id)
          if (image) {
            return { ...image, sortOrder: index + 1 }
          }
          return image
        }).filter(Boolean) as ServiceImage[]
        
        currentService.value.images = reorderedImages
      }
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reorder images'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Service Availability Management
  const getServiceAvailability = async (_serviceId: string) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // const availability = await ServiceService.getServiceAvailability(serviceId)
      
      // For now, return mock data
      const availability: ServiceAvailability[] = []
      
      return availability
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get availability'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const saveServiceAvailability = async (_serviceId: string, availability: ServiceAvailability) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // const savedAvailability = await ServiceService.saveServiceAvailability(serviceId, availability)
      
      return availability
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save availability'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteServiceAvailability = async (_serviceId: string, _availabilityId: string) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // await ServiceService.deleteServiceAvailability(serviceId, availabilityId)
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete availability'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Service Reviews Management
  const getServiceReviews = async (_serviceId: string, page: number = 1, _pageSize: number = 10) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // const reviews = await ServiceService.getServiceReviews(serviceId, { page, pageSize })
      
      // For now, return mock data
      const reviews = {
        reviews: [] as ServiceReview[],
        totalCount: 0,
        totalPages: 0,
        currentPage: page
      }
      
      return reviews
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get reviews'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const respondToReview = async (_serviceId: string, _reviewId: string, _response: string) => {
    setLoading(true)
    clearError()
    
    try {
      // In a real app, this would call the API
      // await ServiceService.respondToReview(serviceId, reviewId, response)
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to respond to review'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Location Management
  const createLocation = async (locationData: CreateLocationRequest) => {
    setLoading(true)
    clearError()
    
    try {
      const location = await ServiceService.createLocation(locationData)
      
      // Add to local state
      locations.value.push(location)
      
      return location
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create location'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateLocation = async (id: string, locationData: UpdateLocationRequest) => {
    setLoading(true)
    clearError()
    
    try {
      const updatedLocation = await ServiceService.updateLocation(id, locationData)
      
      // Update local state
      const index = locations.value.findIndex(l => l.id === id)
      if (index !== -1) {
        locations.value[index] = updatedLocation
      }
      
      return updatedLocation
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update location'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteLocation = async (id: string) => {
    setLoading(true)
    clearError()
    
    try {
      await ServiceService.deleteLocation(id)
      
      // Remove from local state
      locations.value = locations.value.filter(l => l.id !== id)
      
      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete location'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const toggleLocationStatus = async (id: string, activate: boolean) => {
    setLoading(true)
    clearError()
    
    try {
      const updatedLocation = await ServiceService.toggleLocationStatus(id, activate)
      
      // Update local state
      const index = locations.value.findIndex(l => l.id === id)
      if (index !== -1) {
        locations.value[index] = updatedLocation
      }
      
      return updatedLocation
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to toggle location status'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Logo upload methods
  const uploadServiceLogo = async (serviceId: string, logoFile: File) => {
    console.log('Service store - uploading logo for service:', serviceId, 'File:', logoFile)
    setLoading(true)
    clearError()
    
    try {
      const formData = new FormData()
      formData.append('logoFile', logoFile)
      
      console.log('Service store - making request to:', `/services/${serviceId}/logo`)
      console.log('Service store - serviceId type:', typeof serviceId, 'value:', serviceId)
      console.log('Service store - FormData contents:', formData.get('logoFile'))
      
      const result = await api.post(`/services/${serviceId}/logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      
      console.log('Service store - upload result:', result)

      // Invalidate cache by refetching from server
      await getServiceById(serviceId)

      return (result as unknown as { logoUrl: string }).logoUrl
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload logo'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const removeServiceLogo = async (serviceId: string) => {
    setLoading(true)
    clearError()

    try {
      await api.delete(`/services/${serviceId}/logo`)

      // Update current service if it matches
      if (currentService.value?.id === serviceId) {
        currentService.value.logoImageUrl = undefined
      }

      // Update services list
      const serviceIndex = services.value.findIndex(s => s.id === serviceId)
      if (serviceIndex !== -1) {
        services.value[serviceIndex].logoImageUrl = undefined
      }

      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove logo'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Gallery image management
  const uploadServiceImage = async (serviceId: string, imageFile: File) => {
    console.log('Service store - uploading image for service:', serviceId, 'File:', imageFile)
    setLoading(true)
    clearError()

    try {
      const formData = new FormData()
      formData.append('imageFile', imageFile)
      formData.append('isPublic', 'true')

      console.log('Service store - making request to:', `/services/${serviceId}/images/upload`)

      const result = await api.post(`/services/${serviceId}/images/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      console.log('Service store - upload result:', result)

      return result.data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload image'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const removeServiceImage = async (serviceId: string, documentId: string) => {
    setLoading(true)
    clearError()

    try {
      await api.delete(`/services/${serviceId}/documents/${documentId}`)

      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove image'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateServiceLocation = async (serviceId: string, locationId: string | null) => {
    setLoading(true)
    clearError()

    try {
      const result = await ServiceService.updateServiceLocation(serviceId, locationId)

      // Update current service if it matches
      if (currentService.value?.id === serviceId) {
        currentService.value = result
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service location'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateServiceAvailability = async (serviceId: string, availabilities: any[]) => {
    setLoading(true)
    clearError()

    try {
      const result = await ServiceService.updateServiceAvailability(serviceId, availabilities)

      // Update current service if it matches
      if (currentService.value?.id === serviceId) {
        currentService.value = result
      }

      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service availability'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Tag Management Functions
  const addServiceTag = async (serviceId: string, tagData: { tagName: string; tagColor: string; sortOrder: number }) => {
    setLoading(true)
    clearError()

    try {
      const response = await api.post(`/services/${serviceId}/tags`, tagData)

      // Note: tags property may not exist on ServiceDetails
      // Refresh the service to get updated data
      await getServiceById(serviceId)

      return response.data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add tag'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const removeServiceTag = async (serviceId: string, tagId: string) => {
    setLoading(true)
    clearError()

    try {
      await api.delete(`/services/${serviceId}/tags/${tagId}`)

      // Note: tags property may not exist on ServiceDetails
      // Refresh the service to get updated data
      await getServiceById(serviceId)

      return true
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove tag'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const getServiceTags = async (serviceId: string) => {
    setLoading(true)
    clearError()

    try {
      const response = await api.get(`/services/${serviceId}/tags`)
      return response.data
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get tags'
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  // Reset store to initial state (called on logout)
  const $reset = () => {
    services.value = []
    currentService.value = null
    categories.value = []
    locations.value = []
    loading.value = false
    error.value = null
    searchResults.value = null
    searchParams.value = {
      page: 1,
      pageSize: 10,
      sortBy: 'CreatedAt',
      sortDirection: 'desc'
    }
  }

  return {
    // State
    services,
    currentService,
    categories,
    locations,
    loading,
    error,
    searchResults,
    searchParams,
    
    // Computed
    hasServices,
    totalPages,
    currentPage,
    totalCount,
    activeServices,
    draftServices,
    
    // Actions
    setLoading,
    setError,
    clearError,
    searchServices,
    getServiceById,
    getServicesByProvider,
    getMyServices,
    getFeaturedServices,
    createService,
    updateService,
    toggleServiceStatus,
    publishService,
    archiveService,
    deleteService,
    loadCategories,
    loadMyLocations,
    loadLocationsForProvider,
    updateSearchParams,
    resetSearch,
    clearCurrentService,
    
    // Image Management
    uploadServiceImages,
    updateServiceImage,
    deleteServiceImage,
    reorderServiceImages,
    
    // Availability Management
    getServiceAvailability,
    saveServiceAvailability,
    deleteServiceAvailability,
    
    // Reviews Management
    getServiceReviews,
    respondToReview,
    
    // Location Management
    createLocation,
    updateLocation,
    deleteLocation,
    toggleLocationStatus,
    
    // Logo Management
    uploadServiceLogo,
    removeServiceLogo,

    // Gallery Image Management
    uploadServiceImage,
    removeServiceImage,

    // Service Management
    updateServiceLocation,
    updateServiceAvailability,

    // Tag Management
    addServiceTag,
    removeServiceTag,
    getServiceTags,

    // Store Management
    $reset
  }
})